<template>
    <div class="game-container">
        <div class="loader" v-if="!loaded">
            <loader />
        </div>
        <div class="game-container-inner" v-if="loaded">
            <waiting-room v-if="!gameInSession && !gameInDrafting" />
            <draft-area v-if="gameInDrafting" :isMobile="isMobile" />
            <score-board v-if="gameInSession" :gameState="gameState" />
            <div
                class="play-area"
                :class="{
                    ['four-player-game']: numOfPlayers === 4,
                    ['submitting-action']: submittingAction,
                }"
                v-if="gameInSession"
            >
                <span class="undo-wrapper">
                    <button
                        class="btn btn-outline btn-3d"
                        :class="{['disabled']: !canRequestUndo}"
                        :disabled="!canRequestUndo"
                        @click="requestUndo"
                    >
                        <fa-icon class="undo-icon flipped" icon="undo-alt" /> Undo
                    </button>
                </span>

                <span class="sound-control-position" v-if="!isSpectator">
                    <sound classes="btn btn-3d btn-round btn-outline btn-gray" />
                </span>

                <div class="play-section section-left">
                    <player-mat v-if="players.length > 2"
                        :gameState="gameState"
                        :player="playerInPosition('left')"
                        :showHand="false"
                        :unitsInBag="getPlayerUnits(playerInPosition('left'), 'in_bag')"
                        :unitsInHand="getPlayerUnits(playerInPosition('left'), 'in_hand')"
                        :topOfBagUnits="getPlayerUnits(playerInPosition('left'), 'top_of_bag')"
                        position="left"
                    />
                </div>

                <div class="section-middle">
                    <div
                        class="decrees-bar-wrapper"
                        :class="{expanded: decreesBarExpanded}"
                        v-if="decrees.length"
                    >
                        <decrees-bar :decrees="decrees" />
                        <div class="decrees-bar-label" @click="() => toggleDecreesBar()">
                            <span class="label-text">
                                Decrees
                            </span>
                            <fa-icon icon="angle-down" class="icon" />
                        </div>
                    </div>

                    <div class="play-section section-top">
                        <player-mat
                            :gameState="gameState"
                            :player="playerInPosition('top')"
                            :showHand="false"
                            :unitsInBag="getPlayerUnits(playerInPosition('top'), 'in_bag')"
                            :unitsInHand="getPlayerUnits(playerInPosition('top'), 'in_hand')"
                            :topOfBagUnits="getPlayerUnits(playerInPosition('top'), 'top_of_bag')"
                            position="top"
                            v-if="!isMobile"
                        />
                        <mobile-player-mat
                            :actionsLog="actionsLog"
                            :gameState="gameState"
                            :player="playerInPosition('top')"
                            :showHand="false"
                            :unitsInBag="getPlayerUnits(playerInPosition('top'), 'in_bag')"
                            :unitsInHand="getPlayerUnits(playerInPosition('top'), 'in_hand')"
                            :topOfBagUnits="getPlayerUnits(playerInPosition('top'), 'top_of_bag')"
                            position="top"
                            :isBlitzMode="isBlitzMode"
                            v-if="isMobile"
                        />
                    </div>

                    <div class="board-wrapper" ref="board-wrapper">
                        <board :gameState="gameState" />
                        <actions-log :actionsLog="actionsLog" v-if="!isMobile"/>
                    </div>

                    <div class="play-section section-bottom">
                        <player-mat
                            :gameState="gameState"
                            :player="playerInPosition('bottom')"
                            :showHand="!isSpectator"
                            :unitsInBag="unitsInBag"
                            :unitsInHand="unitsInHand"
                            position="bottom"
                            v-if="!isMobile"
                        />
                        <mobile-player-mat
                            :gameState="gameState"
                            :player="playerInPosition('bottom')"
                            :showHand="!isSpectator"
                            :unitsInBag="unitsInBag"
                            :unitsInHand="unitsInHand"
                            :isBlitzMode="isBlitzMode"
                            position="bottom"
                            v-if="isMobile"
                        />
                    </div>

                </div>

                <div class="play-section section-right">
                    <player-mat v-if="players.length > 2"
                        :gameState="gameState"
                        :player="playerInPosition('right')"
                        :showHand="false"
                        :unitsInHand="getPlayerUnits(playerInPosition('right'), 'in_hand')"
                        :unitsInBag="getPlayerUnits(playerInPosition('right'), 'in_bag')"
                        :topOfBagUnits="getPlayerUnits(playerInPosition('right'), 'top_of_bag')"
                        position="right"
                    />
                </div>

            </div>
            <div class="overlay" v-if="actionModal && !actionModalMinimized"></div>
            <action-modal v-if="actionModal" :minimized="actionModalMinimized" :on-toggle-callback="onToggleModal" />
            <undo-modal v-if="showUndoModal" />
            <victory-modal
				v-if="gameHasEnded"
				:show="gameHasEnded"
				:redirect="redirect"
				:gameState="gameState"
				:player="player"
			/>
            <div class="modal modal-rules" v-if="showRulesModal">
                <span class="close-modal-btn" @click="closeRulesModal">
                    <fa-icon class="close-modal-icon" icon="times" />
                </span>
                <rules />
            </div>
            <chat
                v-if="!isSpectator"
                :gameStatus="gameState.state"
                :class="{'game-in-session': gameInSession }"
            />
            <timeout
                :refresh="refreshTimeout"
                v-if="timeLimit &&
                    gameInSession &&
                    !gameHasEnded &&
                    !isSpectator"
				:showWaitingText="gameState.queueMode"
            />
            <timeout-blitz
                v-if="isBlitzMode &&
                    players.length &&
                    gameInSession &&
                    !gameHasEnded"
                :refresh="refreshTimeout"
                :players="players"
                :updatedAt="timeOutUpdatedAt"
                :activePlayerId="timeOutActivePlayerId"
                :gameStateActivePlayerId="gameState.activePlayerId"
                :turn="turn"
                :isMobile="isMobile"
                :theme="gameState.theme"
            />
        </div>
        <notification group="game" />
        <notification group="undo" />
        <div
            class="turn-notification animated"
            :class="{
                [turnNotification.color]: true,
                [isMobile ? 'slide-in-top' : 'slide-in-right']: turnNotification.slideIn,
                [isMobile ? 'slide-out-top' : 'slide-out-right']: turnNotification.slideOut,
            }"
            v-if="turnNotification.show"
        >
            <span class="turn-icon-wrapper">
                <royal-coin
                    :color="turnNotification.color"
                    :theme="gameState.theme"
                    className="turn-icon"
                />
            </span>
            <span class="notification-text">
                {{ turnNotification.text }}
            </span>
        </div>
        <decrees-modal
            :show="decrees.length && showDecreesModal && (gameInDrafting || gameInSession)"
            :gameInDrafting="gameInDrafting"
            :onCloseCallback="dismissDecreesModal"
            :decrees="decrees"
        />
        <mobile-game-menu
            v-if="!isSpectator && gameInSession"
            :gameId="gameId"
            :onUndoCallback="requestUndo"
            :refreshTimeout="refreshTimeout"
        />
        <div class="mobile-chat-icon-wrapper" v-if="!isSpectator && !gameInSession && isMobile && loaded" @click="onShowChat" >
            <fa-icon class="chat-icon" icon="comment"/>
            <span class="unread-messages-count" v-if="unreadMessagesCount">
                {{ unreadMessagesCount > 9 ? '9+' : unreadMessagesCount }}
            </span>
        </div>
        <timeout-modal />
        <actions-log-modal />
        <block-user-modal />
        <cancel-modal
            v-if="showCancelGameModal"
            :onCloseModal="onCloseCancelGameModal"
            :gameState="gameState"
        />
    </div>
</template>

<script>
import find from 'lodash.find';
import moment from 'moment';
import pako from 'pako';
import { IGameStatus, IColor, IDraftType } from './Game-types';
import { MOBILE_MAX_WIDTH } from '../../App-types';
import { isMobileDevice } from '../../helpers/general';

const {
    STARTED,
    DRAFTING,
    ELIMINATION_DRAFTING,
    ENDED,
    SELECTING_DRAFT_ORDER,
    SELECTING_PLAY_ORDER,
    READY_TO_START,
} = IGameStatus;

export default {
    name: 'Game',
    async created() {
        await this.$store.dispatch('setGameLoaded', false);
        await this.$store.dispatch('setUpdatedSkillRating', null);
        await this.$socket.emit('joinRoomChannel', this.gameId);
        await this.$store.dispatch('setActionsLog', []);
        await this.$store.dispatch('setDecrees', []);
        await this.$store.dispatch('setForts', []);
        await this.$store.dispatch('setShowChat', false);

        this.getGameState(true);

        if (this.defaultRedirect === '/matches') {
            this.redirect = '/matches';
        }

        if (!this.socketRefreshInterval) {
            this.socketRefreshInterval = setInterval(async () => {
                if (!this.$socket.connected) {
                    await this.$socket.connect();
                    await this.$socket.emit('joinRoomChannel', this.gameId);
                    await this.getGameState();
                }
            }, 3000);
        }
        window.addEventListener('resize', this.onWindowResize);

        this.showCancelGameModal = false;
    },
    unmounted() {
        window.removeEventListener('resize', this.onWindowResize);
    },
    beforeUnmount() {
        clearInterval(this.socketRefreshInterval);
    },
    data() {
        return {
            actionModalMinimized: false,
            decreesBarExpanded: false,
            turnNotification: {
                color: IColor.WHITE,
                show: false,
                slideIn: false,
                slideOut: false,
                text: '',
            },
            redirect: '/rooms',
            refreshTimeout: moment().format(),
            socketRefreshInterval: null,
            windowWidth: window.innerWidth,
            showCancelGameModal: false,
        };
    },
    watch: {
        activePlayer(activePlayer, prevActivePlayer) {
            let firstTurn = false;
            let playerHasChanged = false;
            let samePlayerNewRound = false;

            if (activePlayer) {
                firstTurn = !activePlayer.turn;

                if (prevActivePlayer) {
                    playerHasChanged = activePlayer.id !== prevActivePlayer.id;
                    samePlayerNewRound = activePlayer.id === prevActivePlayer.id &&
                        activePlayer.turn !== prevActivePlayer.turn;
                }

                if (firstTurn || playerHasChanged || samePlayerNewRound || this.gameInDrafting) {
                    this.showTurnNotification(activePlayer);
                }
            }
        },
        selectedUnitId(unitId, prevUnitId) {
            if (!this.decrees.length) {
                return;
            }

            if (unitId !== prevUnitId) {
                const prevUnitDecreeActions = [];
                const unitDecreeActions = [];

                for (const action of this.actions) {
                    if (this.decreeTypes.includes(action.type.toUpperCase())) {
                        if (action.unitId === unitId) {
                            unitDecreeActions.push(action);
                        } else if (action.unitId === prevUnitId) {
                            prevUnitDecreeActions.push(action);
                        }
                    }
                }

                if (!prevUnitDecreeActions.length && unitDecreeActions.length && !this.decreesBarExpanded) {
                    this.decreesBarExpanded = true;
                }

                if (prevUnitDecreeActions.length && !unitDecreeActions.length && this.decreesBarExpanded) {
                    this.decreesBarExpanded = false;
                }
            }
        },
        ['gameState.state'](current, prev) {
            if (this.shouldShowCancelMatchModal(prev, current)) {
                this.showCancelGameModal = true;
            }
        },
		gameId:  async function updateGameState(newGameId, oldGameId) {
			if (newGameId !== oldGameId) {
				if (this.$socket.connected) {
					await this.$socket.emit('joinRoomChannel', newGameId);
				}
			}

			await this.getGameState(newGameId !== oldGameId);
    	}
    },
    computed: {
        actionsLog() {
            return this.$store.state.actionsLogState.actionsLog;
        },
        actionModal() {
            return this.$store.state.actionsState.actionModal;
        },
        activePlayer() {
            const {
                activePlayerId,
                draftingPlayerId,
            } = this.gameState;
            const identifier = this.gameInSession || this.playersChoosingOrder ?
                activePlayerId :
                draftingPlayerId;
            return this.players.filter(player => player.id === identifier)[0];
        },
        canRequestUndo() {
            return this.undoState.canRequestUndo;
        },
        chatState() {
            return this.$store.state.chatState;
        },
        isCreator() {
            return this.gameState.creatorId === this.userId;
        },
        decrees() {
            return this.gameState.decrees;
        },
        decreeTypes() {
            return this.gameState.decrees.map(decree => decree.type);
        },
        defaultRedirect() {
            const { redirect } = this.$route.params;
            return redirect ? String(redirect).toLowerCase() : null;
        },
        gameHasEnded() {
            return this.gameState.state === ENDED;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        gameInDrafting() {
            return [
                DRAFTING,
                ELIMINATION_DRAFTING,
                SELECTING_DRAFT_ORDER,
                SELECTING_PLAY_ORDER,
            ].includes(this.gameState.state);
        },
        gameInSession() {
            return [STARTED, ENDED].includes(this.gameState.state);
        },
        gameState() {
            return this.$store.state.gameState;
        },
        isBlitzMode() {
            return this.gameState.blitz;
        },
        isMobile() {
            return this.windowWidth <= MOBILE_MAX_WIDTH || isMobileDevice();
        },
        isSpectator() {
            return this.$store.getters.isSpectator;
        },
        loaded() {
            return this.gameState.loaded;
        },
        messages() {
            return this.chatState.messages;
        },
        numOfPlayers() {
            return this.gameState.numOfPlayers;
        },
        playersChoosingOrder() {
            return [SELECTING_DRAFT_ORDER, SELECTING_PLAY_ORDER].includes(this.gameState.state);
        },
        players() {
            return this.gameState.players;
        },
        player() {
            return this.$store.getters.player;
        },
        actions() {
            return this.$store.state.actionsState.actions;
        },
        readMessages() {
            return this.chatState.readMessages;
        },
        selectedUnitId() {
            return this.$store.state.actionsState.selectedUnitId;
        },
        showDecreesModal() {
            return this.$store.state.decreesBarState.showDecreesModal;
        },
        showRulesModal() {
            return this.$store.state.modalState.modals.rules.show;
        },
        showUndoModal() {
            return this.$store.state.undoState.showUndoModal;
        },
        submittingAction() {
            return this.$store.state.actionsState.submitting;
        },
        timeOutUpdatedAt() {
            return this.$store.state.actionsState.lastActionTime || this.gameState.updatedAt;
        },
        timeOutActivePlayerId() {
            return this.$store.state.actionsState.decidingPlayerId || this.gameState.activePlayerId;
        },
        timeLimit() {
            return this.gameState.timeLimit;
        },
        undoState() {
            return this.$store.state.undoState;
        },
        unitsInBag() {
            return this.gameState.unitsInBag;
        },
        unitsInHand() {
            return this.gameState.unitsInHand;
        },
        topOfBagUnits() {
            return this.gameState.topOfBagUnits;
        },
        unreadMessagesCount() {
            return this.messages.length - this.readMessages.length;
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        turn() {
            const turns = this.players.map(player => player.turn);
            const reducer = (accumulator, playerTurns) => accumulator + playerTurns;
            const totalTurns = turns.reduce(reducer);
            return totalTurns;
        },
    },
    methods: {
        closeRulesModal() {
            this.$store.dispatch('closeModal', 'rules');
        },
        isActivePlayer() {
            return this.activePlayer.id === this.player.id && !this.isSpectator;
        },
        dismissDecreesModal() {
            this.$store.dispatch('setShowDecreesModal', false);
        },
        async getGameState(resetSelections = false) {
            await this.$store.dispatch('getGameState', this.gameId);

            if (resetSelections) {
                await this.$store.dispatch('resetSelections');
                await this.$store.dispatch('setUnitTypes', []);
            }

            if (this.gameInDrafting) {
                await this.$store.dispatch('getDraftUnits', this.gameId);
                await this.$store.dispatch('getExpansionsData', this.gameId);
            }

            if (this.gameInSession) {
                await this.$store.dispatch('getUnitsData', this.player.id);
                await this.$store.dispatch('getExpansionsData', this.gameId);
                await this.$store.dispatch('getActions', this.player.id);
                await this.$store.dispatch('getActionsLog', this.gameId);
                await this.$store.dispatch('getUndoState', this.gameId);

                if (!this.gameState.unitTypes.length) {
                    await this.$store.dispatch('getUnitTypes', this.gameId);
                }
            }
        },
        getPlayerUnits(player, state) {
            return player?.units.filter(unit => unit.state === state) ?? [];
        },
        onCloseCancelGameModal() {
            this.showCancelGameModal = false;
        },
        onShowChat() {
            this.$store.dispatch('setShowChat', true);
        },
        onToggleModal(type) {
            const dataKey = `${type}ModalMinimized`;
            this[dataKey] = !this[dataKey];
        },
        playerInPosition(position) {
            const turnOrder = this.gameState.turnOrder;
            const numOfPlayers = this.gameState.numOfPlayers;
            let playerId = this.player.id;
            const index = turnOrder.indexOf(playerId);
            let offset;

            switch (position) {
                case 'top':
                    offset = numOfPlayers === 2 ? 1 : 2;
                    break;
                case 'right':
                    offset = 3;
                    break;
                case 'left':
                    offset = 1;
                    break;
                default:
                    offset = 0;
            }

            if (index + offset >= turnOrder.length) {
                playerId = turnOrder[index + offset - turnOrder.length];
            } else {
                playerId = turnOrder[index + offset];
            }

            return find(this.players, { id: playerId });
        },
        requestUndo() {
            this.$store.dispatch('requestUndo', this.gameId);
        },
        showTurnNotification(activePlayer) {
            let turnNotificationText = this.isActivePlayer()
                ? 'Your turn'
                : `${activePlayer.user.username}'s turn`;

            if (this.gameInDrafting) {
                let activeDescription = '';
                let inactiveDescription = '';

                switch (this.gameState.state) {
                    case IGameStatus.ELIMINATION_DRAFTING:
                        activeDescription = 'eliminate';
                        inactiveDescription = 'eliminating';
                        break;
                    case IGameStatus.SELECTING_DRAFT_ORDER:
                    case IGameStatus.SELECTING_PLAY_ORDER:
                        activeDescription = 'make a choice';
                        inactiveDescription = 'making a choice';
                        break;
                    default:
                        activeDescription = 'draft';
                        inactiveDescription = 'drafting';
                }

                turnNotificationText = this.isActivePlayer()
                    ? `Your turn to ${activeDescription}`
                    : `${activePlayer.user.username} is ${inactiveDescription}`;
            }

            this.turnNotification.color = activePlayer.team.color;
            this.turnNotification.text = turnNotificationText;
            this.turnNotification.show = true;
            this.turnNotification.slideIn = true;

            setTimeout(() => {
                this.turnNotification.slideIn = false;
                this.turnNotification.slideOut = true;
            }, 2000);

            setTimeout(() => {
                this.turnNotification.show = false;
                this.turnNotification.slideOut = false;
            }, 2500);

            if (this.isActivePlayer()) {
                this.$store.dispatch('playSound');
            }
        },
        toggleDecreesBar(value) {
            this.decreesBarExpanded = value ? value : !this.decreesBarExpanded;
        },
        onWindowResize() {
            this.windowWidth = window.innerWidth;
        },
        shouldShowCancelMatchModal(prevState, currentState) {
            return !this.isCreator &&
                !this.isSpectator &&
                prevState === READY_TO_START && currentState === STARTED &&
                this.gameState.numOfPlayers === 2 &&
                this.gameState.draftType === IDraftType.RANDOM &&
                this.gameState.settingsUpdatedAt &&
                Math.abs(moment(this.gameState.startedAt).diff(this.gameState.settingsUpdatedAt) / 1000) <= 30;
        },
    },
    sockets: {
        async onUpdateGameState(compressedState) {
            const gameState = JSON.parse(pako.inflate(compressedState, { to: 'string' }));

            if (gameState.id !== this.gameId) {
                return;
            }

            if (this.gameInSession) {
                this.refreshTimeout = moment().format();
            }

            this.$store.dispatch('setGameState', gameState);

            if (gameState.state === IGameStatus.CANCELLED) {
                return;
            }

            if (this.gameInDrafting) {
                await this.$store.dispatch('getDraftUnits', this.gameId);
                await this.$store.dispatch('getExpansionsData', this.gameId);
            }

            if (this.gameInSession) {
                await this.$store.dispatch('getUnitsData', this.player.id);
                await this.$store.dispatch('getExpansionsData', this.gameId);
                await this.$store.dispatch('getActions', this.player.id);
                await this.$store.dispatch('getActionsLog', this.gameId);
                await this.$store.dispatch('getUndoState', this.gameId);

                if (!this.gameState.unitTypes.length) {
                    await this.$store.dispatch('getUnitTypes', this.gameId);
                }
            }
        },
		async onPlayerJoinGame(redirectGameId) {
			this.$router.push(`/game/${redirectGameId}`);

			this.$notify({
				group: 'app',
				title: 'Success',
				type: 'success',
				text: 'A player joined your room!',
			});
		}
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .game-container {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        position: relative;
        text-align: center;

        &.dark-mode {
            background-color: #121212; // Dark background color
            color: #e0e0e0; // Light text color

            .score-board {
                background-color: #333333; // Dark background

                :deep(.back-btn) {
                    color: #e0e0e0; // Light text color
                }
            }

            .loader, .modal, .chat, .notification {
                background-color: #1e1e1e; // Darker background for these components
                color: #e0e0e0; // Light text color
            }

            .play-area {
                background-color: #1e1e1e; // Dark background for play area
            }

            .turn-notification {
                background: #333333; // Dark background
                color: #e0e0e0; // Light text color
                border-left: 5px solid #444444; // Dark border color
            }

            .undo-wrapper .btn-3d {
                background-color: #333333; // Dark background for buttons
                color: #e0e0e0; // Light text color
                box-shadow: 0 3px #444444; // Dark shadow for buttons

                &.disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                &:hover {
                    box-shadow: 0 1px #444444; // Dark shadow on hover
                }
            }

            .sound-control-position .btn {
                background-color: #333333; // Dark background for sound control
                color: #e0e0e0; // Light text color
            }

            .modal.modal-rules {
                background: #1e1e1e; // Dark background for modal rules
                color: #e0e0e0; // Light text color

                .close-modal-btn .close-modal-icon {
                    color: #e0e0e0; // Light color for close button icon
                }
            }

            .mobile-chat-icon-wrapper {
                background: #333333; // Dark background for chat icon wrapper
                border: 1px solid #444444; // Darker border color
                box-shadow: 0px 0px 2px 1px rgba(0, 0, 0, 0.5); // Dark shadow

                .chat-icon {
                    color: #e0e0e0; // Light color for chat icon
                }
                .unread-messages-count {
                    background: #e0e0e0; // Light background for unread messages count
                    color: #121212; // Dark text color
                }
            }

            .actions ul li {
                color: #e0e0e0; // Light color for actions list items
            }

            :deep(.tile::before) {
                background-color: lightgray; // Darker background for these components
            }

            :deep(.tile.dark::before) {
                background-color: gray; // Darker background for these components
            }
        }

        .game-container-inner {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
        }

        .play-area {
            box-sizing: border-box;
            display: flex;
            height: 0;
            flex-grow: 1;
            justify-content: center;
            padding-top: 15px;
            position: relative;

            &.four-player-game {

                .section-middle {

                    .section-bottom,
                    .section-top {

                        .player-mat {
                            max-width: 80%;

                            &.bottom,
                            &.top {

                                .group {

                                    .unit-stack {
                                        max-width: 22%;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            .play-section {
                margin: 0 auto;
            }

            .section-middle {
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                width: 100%;

                > * {
                    align-self: flex-start;
                    display: flex;
                    justify-content: center;
                    width: 100%;
                }

                .section-bottom,
                .section-top {
                    height: 20%;
                    max-height: 20%;

                    .player-mat {
                        align-self: center;
                        max-width: 75%;
                    }
                }

                .board-wrapper {
                    max-height: 60%;
                }

                .decrees-bar-wrapper {
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    left: 50%;
                    position: absolute;
                    top: 0;
                    transform: translateX(-50%) translateY(calc(-100% + 27px));
                    transition: transform 300ms cubic-bezier(0.2, 0, 0, 1) 0s;
                    width: 50%;
                    z-index: 10;

                    &.expanded {
                        transform: translate(-50%, 0);
                        max-width: 580px;
                        width: 100%;

                        .icon {
                            transform: rotate(-180deg);
                        }

                        .decrees-bar {
                            padding: 8px 0;
                            visibility: visible;

                            .decrees-bar-content {
                                display: flex;

                                .decree-widget-wrapper {

                                    &:last-of-type {
                                        margin-right: 0;
                                    }
                                }
                            }
                        }

                        .decrees-bar-label {
                            background: white;
                            border-radius: 4px;
                        }
                    }

                    .decrees-bar-label {
                        align-self: center;
                        background: $light-beige;
                        border-top-width: 0;
                        border-bottom-left-radius: 4px;
                        border-bottom-right-radius: 4px;
                        box-shadow: 0px 1px 3px 1px rgba(123,125,125,0.25);
                        color: #2c3e50;
                        cursor: pointer;
                        font-size: .9em;
                        padding: 4px 10px;

                        .icon {
                            align-self: center;
                            margin-left: 5px;
                            transition: transform 300ms cubic-bezier(0.2, 0, 0, 1) 0s;
                            vertical-align: middle;
                        }
                    }

                    .decrees-bar {
                        visibility: hidden;
                    }
                }

                .board-wrapper {
                    display: flex;
                    flex-direction: column;
                    flex-grow: 1;
                    position: relative;

                    .game-board-wrapper {
                        margin: 0 auto;
                        order: 1;
                        width: 80%;
                    }
                }
            }

            .section-left,
            .section-right {
                align-self: center;
                max-width: 10%;
            }

            .section-left {
                padding-left: 10px;
            }

            .section-right {
                padding-right: 10px;
            }
        }

        .actions {
            margin: 10px auto 25px;

            ul {
                list-style-type: none;
                padding: 0;
                margin: 0;

                li {
                    display: inline-block;

                    &:not(:last-of-type) {
                        margin-right: 20px;
                    }
                }
            }
        }

        .loader {
            display: flex;
            height: 100vh;
            justify-content: center;
            align-items: center;
        }

        .score-board {
            padding: 5px 20px;
        }

        .chat {
            position: fixed;
            bottom: 0;

            &.game-in-session {
                position: absolute;
                bottom: 44px;
            }

            &:not(.expanded) {
                border: none;
            }
        }

        .turn-notification {
            background: white;
            box-sizing: border-box;
            box-shadow: 0px 1px 3px -1px #8f8e8e;
            color: $dark-brown;
            font-family: 'RobotoSlab', 'Arial';
            font-size: 1em;
            padding: 10px 15px;
            position: fixed;
            text-align: center;
            top: 0;
            vertical-align: middle;
            white-space: nowrap;
            width: 100%;
            z-index: 200;

            &.black {
                color: $dark-gray;
            }

            .turn-icon-wrapper {
                display: inline-block;
                vertical-align: middle;
                margin-right: 5px;

                .turn-icon {
                    height: auto;
                    height: 30px;
                    width: 30px;
                    vertical-align: middle;
                    width: auto;
                }
            }

            .notification-text {
                display: inline-block;
                vertical-align: middle;
                text-transform: uppercase;
            }
        }

        .undo-wrapper {
            display: none;
            font-weight: bold;
            left: 25px;
            position: absolute;
            z-index: 3;

            .btn {

                &.btn-3d {
                    .undo-icon {
                        margin-right: 3px;
                    }

                    box-shadow: 0 3px $medium-gray;

                    &:hover {
                        box-shadow: 0 1px $medium-gray;
                    }

                    &.disabled {
                        opacity: .6;
                        cursor: not-allowed;

                        &:hover {
                            top: 0;
                            box-shadow: 0 3px $medium-gray;
                        }
                    }
                }
            }
        }

        .sound-control-position {
            display: none;
            position: absolute;
            right: 25px;
            z-index: 2;
        }

        .modal {

            &.modal-rules {
                height: calc(100% - 46px);
                left: 0;
                top: 0;
                transform: none;
                text-align: left;

                .rules {
                    overflow: auto;
                    padding: 0;
                    margin: 0;
                    height: 100%;
                }

                .close-modal-btn {
                    cursor: pointer;
                    position: absolute;
                    right: 20px;
                    top: 15px;

                    .close-modal-icon {
                        font-size: 1.5em;
                        color: $dark-brown;
                    }
                }
            }
        }

        .timeout-wrapper {
            display: none;
            position: fixed;
            bottom: 25px;
            left: 25px;
            z-index: 9;
        }

        .mobile-chat-icon-wrapper {
            background: $off-white;
            border: 1px solid rgba(119,109,109,0.35);
            border-radius: 50%;
            bottom: 15px;
            box-shadow: 0px 0px 2px 1px rgba(175,175,175,0.35);
            cursor: pointer;
            padding: 6px;
            position: fixed;
            right: 20px;

            .chat-icon {
                color: $sage-brown;
                display: block;
                font-size: 1.3em;
            }

            .unread-messages-count {
                background: $teal;
                border-radius: 50%;
                color: white;
                display: inline-block;
                font-size: .85em;
                height: 12px;
                line-height: 12px;
                padding: 4px;
                position: absolute;
                right: 0;
                top: 0;
                transform: translate(35%, -35%);
                vertical-align: middle;
                width: 12px;
            }
        }
    }

    @media only screen and (min-width: $screen-xxs) {

        .game-container {

            .play-area {

                .section-middle {

                    .board-wrapper {

                        .game-board-wrapper {
                            width: 100%;
                        }
                    }
                }
            }
        }
    }

    @media only screen and (min-width: $screen-xs) {

        .game-container {

            .modal {

                &.modal-rules {
                    left: 50%;
                    top: 50%;
                    height: 90%;
                    max-width: 900px;
                    width: 90%;
                    transform: translateX(-50%) translateY(-50%);
                }
            }

            .sound-control-position {
                display: block;
            }

            .chat {
                position: fixed;
                bottom: 25px;
                right: 25px;

                &:not(.expanded) {
                    border: 1px solid $light-gray;
                }
            }

            .undo-wrapper {
                display: block;
            }

            .play-area {
                height: 100%;

                .section-middle {
                    justify-content: center;

                    .section-bottom,
                    .section-top {
                        height: 17%;
                        max-height: 17%;
                    }

                    .board-wrapper {
                        max-height: 65%;
                    }
                }
            }

            .timeout-wrapper {
                display: block;
            }

            .turn-notification {
                border-bottom: none;
                border-left: 5px solid $coin-border;
                box-shadow: 0px 1px 3px 1px rgba(123,125,125,0.25);
                font-size: 1.2em;
                padding: 17px 21px;
                right: 25px;
                right: 5%;
                top: 90px;
                width: auto;

                &.black {
                    border-left: 5px solid $dark-gray;
                }
            }

            .mobile-chat-icon-wrapper,
            .view-cards-wrapper {
                display: none;
            }
        }
    }

    @media only screen and (min-width: $screen-sm) {

        .game-container {

            .chat {
                bottom: 3.5%;
                right: 2.5%;

                &.game-in-session {
                    bottom: 3.5%;
                    right: 2.5%;
                }
            }

            .play-area {

                .section-middle {

                    .section-bottom,
                    .section-top {

                        .player-mat {
                            align-self: center;
                        }
                    }

                    .board-wrapper {

                        .game-board-wrapper {
                            width: 95%;
                        }
                    }
                }
            }

            .timeout-wrapper {
                bottom: 3.5%;
            }
        }
    }

    @media only screen and (min-width: $screen-lg) {
        .game-container {

            .play-area {

                .section-middle {

                    .section-bottom,
                    .section-top {

                        .player-mat {
                            max-width: 60%;
                        }
                    }

                    .board-wrapper {

                        .game-board-wrapper {
                            width: 100%;
                        }
                    }
                }
            }
        }
    }

</style>
